import React from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink } from "react-router"

import { RootPath } from "@/models/route"
import { TextField } from "@/nzds/inputs/text-field"
import { emailSchema } from "@/schemas"
import { useSSOForInternalUsers } from "@/services/sso"
import { httpClient } from "@/utils/api"
import type { SuccessData } from "@/utils/api"
import { APP_NOREPLY_EMAIL } from "@/utils/constants"
import { useMutation } from "@tanstack/react-query"

import CloseIcon from "@mui/icons-material/Close"
import {
  Alert,
  Box,
  Button,
  Collapse,
  Fade,
  IconButton,
  Link,
  Typography,
} from "@mui/material"

import { If } from "../if/if"
import { WhiteBox } from "../whiteBox"

const sendForgotPasswordEmail = async (
  params: { email: string },
  locale: string
): Promise<SuccessData> => {
  try {
    const response = await httpClient.post<SuccessData>(
      "/api/v1/forgot_password",
      { ...params, locale }
    )

    return response.data
  } catch (error: unknown) {
    console.error("Error sending forgot password email")
    throw error
  }
}

type ResendStatus = "success" | "unknown" | "warning"
interface ForgotPasswordMutationParams {
  requestEmail: string
  locale: string
}

export const ForgotPassword: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [email, setEmail] = React.useState("")
  const [hasSubmitted, setHasSubmitted] = React.useState(false)
  const [hasRequestError, setHasRequestError] = React.useState(false)
  const [resendStatus, setResendStatus] =
    React.useState<ResendStatus>("unknown")
  const forgotPasswordMutation = useMutation({
    mutationFn: async ({
      requestEmail,
      locale,
    }: ForgotPasswordMutationParams) =>
      sendForgotPasswordEmail({ email: requestEmail }, locale),
  })

  const { isSSORequiredForInternalUsers } = useSSOForInternalUsers({ email })

  const isValidEmail: boolean = React.useMemo(() => {
    if (!email.length) {
      return true
    }

    return emailSchema.safeParse(email).success
  }, [email])

  const shouldShowInvalidEmailError: boolean = email.length > 0 && !isValidEmail

  const handleAlertClose = (): void => {
    setHasRequestError(false)
  }

  const handleEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setHasRequestError(false)
    setEmail(event.currentTarget.value)
  }

  const handleForgotPassword = async ({
    isResend = false,
  }: { isResend?: boolean } = {}): Promise<void> => {
    if (forgotPasswordMutation.isPending) {
      return
    }

    setHasRequestError(false)

    if (isResend) {
      setResendStatus("unknown")
    }

    try {
      await forgotPasswordMutation.mutateAsync({
        requestEmail: email,
        locale: i18n.language,
      })

      if (isResend) {
        setResendStatus("success")
      } else {
        setHasSubmitted(true)
      }
    } catch {
      if (isResend) {
        setResendStatus("warning")
      } else {
        setHasRequestError(true)
      }
    }
  }

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault()

    if (!email.length || !isValidEmail || isSSORequiredForInternalUsers) {
      return
    }

    void handleForgotPassword()
  }

  const emailHelperText: string = React.useMemo(() => {
    if (isSSORequiredForInternalUsers) {
      return t("forgotPassword.mustUseSSOError")
    }

    if (shouldShowInvalidEmailError) {
      return t("forgotPassword.invalidEmailError")
    }

    return " "
  }, [isSSORequiredForInternalUsers, shouldShowInvalidEmailError, t])

  return (
    <Fade in={true}>
      <div>
        <WhiteBox aria-labelledby="forgotPasswordTitle">
          <Typography
            align="center"
            id="forgotPasswordTitle"
            variant="h1"
            sx={{
              mb: 4,
            }}
          >
            {t("forgotPassword.title")}
          </Typography>

          <Collapse in={hasRequestError}>
            <Box
              sx={{
                pb: 2,
              }}
            >
              <Alert
                action={
                  <IconButton
                    aria-label={t("button.close")}
                    color="inherit"
                    size="small"
                    onClick={handleAlertClose}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                severity="warning"
                sx={(theme) => ({
                  backgroundColor: theme.palette.background.paper,
                })}
                variant="outlined"
              >
                {t("forgotPassword.requestError")}
              </Alert>
            </Box>
          </Collapse>

          <If condition={!hasSubmitted}>
            <Typography
              sx={{
                mb: 3,
              }}
            >
              {t("forgotPassword.instructions", {
                fromEmail: APP_NOREPLY_EMAIL,
              })}
            </Typography>
            <form aria-labelledby="forgotPasswordTitle" onSubmit={handleSubmit}>
              <Box
                sx={{
                  mb: 2,
                }}
              >
                <TextField
                  error={
                    isSSORequiredForInternalUsers || shouldShowInvalidEmailError
                  }
                  fullWidth
                  helperText={emailHelperText}
                  id="email"
                  label={t("fields.email.title")}
                  onChange={handleEmailChange}
                  slotProps={{
                    htmlInput: {
                      type: "email",
                    },
                  }}
                  value={email}
                  variant="stacked"
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  textAlign: "center",
                }}
              >
                <Button
                  color="primary"
                  disabled={
                    !email.length ||
                    !isValidEmail ||
                    forgotPasswordMutation.isPending ||
                    isSSORequiredForInternalUsers
                  }
                  fullWidth
                  id="submit-button"
                  type="submit"
                  variant="contained"
                >
                  {t("forgotPassword.sendResetLinkButton")}
                </Button>
                <Link component={RouterLink} to={`/${RootPath.SignIn}`}>
                  {t("forgotPassword.backToSignInLink")}
                </Link>
              </Box>
            </form>
          </If>

          <If condition={hasSubmitted}>
            <Typography
              align="center"
              sx={{
                mb: 3,
              }}
            >
              {t("forgotPassword.successMessage", { email })}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                textAlign: "center",
              }}
            >
              <Button
                color="primary"
                disabled={forgotPasswordMutation.isPending}
                onClick={() => {
                  void handleForgotPassword({ isResend: true })
                }}
                type="button"
                variant="contained"
              >
                {t("forgotPassword.resendEmailButton")}
              </Button>
              <Button
                onClick={() => {
                  setHasSubmitted(false)
                  setHasRequestError(false)
                  setResendStatus("unknown")
                }}
                type="button"
                variant="outlined"
              >
                {t("forgotPassword.useDifferentEmailButton")}
              </Button>
              <Link component={RouterLink} to={`/${RootPath.SignIn}`}>
                {t("forgotPassword.backToSignInLink")}
              </Link>
            </Box>
            <Collapse in={resendStatus === "success"}>
              <Box
                sx={{
                  pt: 2,
                }}
              >
                <Alert severity="success">
                  {t("forgotPassword.resendSuccessMessage")}
                </Alert>
              </Box>
            </Collapse>
            <Collapse in={resendStatus === "warning"}>
              <Box
                sx={{
                  pt: 2,
                }}
              >
                <Alert severity="warning">
                  {t("forgotPassword.requestError")}
                </Alert>
              </Box>
            </Collapse>
          </If>
        </WhiteBox>
      </div>
    </Fade>
  )
}
